import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Button, Flex, Spinner, Text } from "@radix-ui/themes";
import {
  useGetZendeskTicketGroups,
  useUpdateZendeskTicketGroups,
} from "@/api/integration/Integration.api";
import { ZendeskGroup } from "@/api/integration/Integration.model";
import Checkbox from "@/components/shared/inputs/checkbox";
import { toast } from "sonner";

interface ZendeskTicketIntegrationProps {
  onClose: () => void;
  onSave: () => void;
}

export function ZendeskTicketIntegration({
  onClose,
  onSave,
}: ZendeskTicketIntegrationProps) {
  const {
    data,
    isLoading: isLoadingGroups,
    error,
  } = useGetZendeskTicketGroups();
  const zendeskGroups = data?.groups ?? [];

  const updateGroupsMutation = useUpdateZendeskTicketGroups();

  // State to track the current selections (initially based on isIncludePolicy)
  const [groupSelections, setGroupSelections] = useState<
    Record<string, boolean>
  >({});

  // Track original selections to detect changes
  const [originalSelections, setOriginalSelections] = useState<
    Record<string, boolean>
  >({});

  // Initialize selections based on API data
  useEffect(() => {
    if (zendeskGroups) {
      const initialSelections: Record<string, boolean> = {};
      zendeskGroups.forEach((group) => {
        initialSelections[group.externalId] = group.isIncludePolicy;
      });
      setGroupSelections(initialSelections);
      setOriginalSelections(initialSelections);
    }
  }, [zendeskGroups]);

  const handleGroupToggle = useCallback(
    (externalId: string, checked: boolean) => {
      setGroupSelections((prev) => ({
        ...prev,
        [externalId]: checked,
      }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!zendeskGroups) return;

    try {
      // Only include groups that have changed from their original state
      const changedGroups = zendeskGroups
        .filter((group) => {
          const currentSelection = groupSelections[group.externalId] ?? false;
          const originalSelection =
            originalSelections[group.externalId] ?? false;
          return currentSelection !== originalSelection;
        })
        .map((group) => ({
          externalId: group.externalId,
          name: group.name,
          isIncludePolicy: groupSelections[group.externalId] ?? false,
        }));

      console.log("Changed groups:", changedGroups);

      if (changedGroups.length === 0) {
        toast.info("No changes to save");
        onSave();
        return;
      }

      await updateGroupsMutation.mutateAsync(changedGroups);

      // Update the original selections to match current after successful save
      setOriginalSelections({ ...groupSelections });

      toast.success(
        `Successfully updated ${changedGroups.length} group${
          changedGroups.length === 1 ? "" : "s"
        }`
      );
      onSave();
    } catch (error) {
      console.error("Failed to save configuration:", error);
      toast.error("Failed to save configuration. Please try again.");
    }
  }, [
    zendeskGroups,
    groupSelections,
    originalSelections,
    updateGroupsMutation,
    onSave,
  ]);

  const handleSelectAll = useCallback(() => {
    if (zendeskGroups) {
      const allSelected: Record<string, boolean> = {};
      zendeskGroups.forEach((group) => {
        allSelected[group.externalId] = true;
      });
      setGroupSelections(allSelected);
    }
  }, [zendeskGroups]);

  const handleSelectNone = useCallback(() => {
    if (zendeskGroups) {
      const noneSelected: Record<string, boolean> = {};
      zendeskGroups.forEach((group) => {
        noneSelected[group.externalId] = false;
      });
      setGroupSelections(noneSelected);
    }
  }, [zendeskGroups]);

  // Calculate statistics
  const selectedCount = useMemo(() => {
    return Object.values(groupSelections).filter(Boolean).length;
  }, [groupSelections]);

  // Calculate changed groups count
  const changedCount = useMemo(() => {
    return zendeskGroups.filter((group) => {
      const currentSelection = groupSelections[group.externalId] ?? false;
      const originalSelection = originalSelections[group.externalId] ?? false;
      return currentSelection !== originalSelection;
    }).length;
  }, [zendeskGroups, groupSelections, originalSelections]);

  const totalCount = zendeskGroups?.length ?? 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;
  const noneSelected = selectedCount === 0;
  const hasChanges = changedCount > 0;

  // Error Handling
  if (error) {
    return (
      <Dialog.Root open onOpenChange={onClose}>
        <Dialog.Content style={{ maxWidth: 500 }}>
          <Dialog.Title>Configure Zendesk Ticket Groups</Dialog.Title>
          <Text color="red" size="2">
            Failed to load Zendesk groups. Please try again later.
          </Text>
          <Flex gap="3" mt="4" justify="end">
            <Button variant="soft" color="gray" onClick={onClose}>
              Close
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 500, maxHeight: "80vh" }}>
        <Dialog.Title>Configure Zendesk Ticket Groups</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Select which ticket groups to include in your Zendesk integration.
          Groups marked as included will be processed by Quack.
        </Dialog.Description>

        {isLoadingGroups ? (
          <Flex justify="center" py="6">
            <Spinner size="3" />
            <Text size="2" ml="2">
              Loading groups...
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="4">
            {/* Group selection */}
            <div>
              <Flex justify="between" align="center" mb="3">
                <Flex direction="column" gap="1">
                  <Text size="3" weight="medium">
                    Select Groups ({selectedCount} of {totalCount})
                  </Text>
                  {hasChanges && (
                    <Text size="1" color="orange">
                      {changedCount} unsaved change
                      {changedCount === 1 ? "" : "s"}
                    </Text>
                  )}
                </Flex>
                <Flex gap="2">
                  <Button
                    variant="soft"
                    size="1"
                    onClick={handleSelectAll}
                    disabled={allSelected}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="soft"
                    size="1"
                    onClick={handleSelectNone}
                    disabled={noneSelected}
                  >
                    Select None
                  </Button>
                </Flex>
              </Flex>

              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid var(--gray-6)",
                  borderRadius: "6px",
                  padding: "12px",
                }}
              >
                {!zendeskGroups || zendeskGroups.length === 0 ? (
                  <Text size="2" color="gray">
                    No groups found in your Zendesk account.
                  </Text>
                ) : (
                  <Flex direction="column" gap="2">
                    {zendeskGroups.map((group) => (
                      <div key={group.externalId} style={{ padding: "4px 0" }}>
                        <Checkbox
                          label={group.name}
                          value={groupSelections[group.externalId] ?? false}
                          onChange={(checked) =>
                            handleGroupToggle(group.externalId, checked)
                          }
                        />
                      </div>
                    ))}
                  </Flex>
                )}
              </div>
            </div>
          </Flex>
        )}

        <Flex gap="3" mt="4" justify="end">
          <Button variant="soft" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            highContrast
            onClick={handleSave}
            loading={updateGroupsMutation.isLoading}
            disabled={isLoadingGroups || !hasChanges}
          >
            {hasChanges
              ? `Save ${changedCount} Change${changedCount === 1 ? "" : "s"}`
              : "No Changes to Save"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
